import { t } from "i18next";
import * as z from "zod";

import {
  CUSTOMER_SEGMENTS,
  FEE_CALCULATION_TYPES,
  FEE_PAYERS_FEE_ENGINE,
  FEE_PURPOSES,
  FEE_RULE_STATUSES,
  TIER_BASIS,
} from "@/features/shared/api/enums";

/**
 * Schéma du formulaire de création d'une règle de frais
 * (`POST /FeeRules/add`, guide §6.5). Les montants sont saisis en unité
 * MAJEURE ; la conversion vers `*Minor` (entier, unité mineure) est faite
 * au submit via `majorToMinor`.
 *
 * `FEE_PAYERS_FEE_ENGINE` (avec SPLIT) — jamais `FEE_PAYERS_WALLET` : la
 * liste admise diffère entre wallet-service et fee-engine-service
 * (annexe A).
 *
 * Factory (règle projet) : créé via `createFeeRuleSchema()` et mémoïsé au
 * niveau composant sur la langue courante — jamais d'instance exportée.
 */
export const createFeeRuleSchema = () =>
  z.object({
    productCode: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    providerId: z
      .string()
      .trim()
      .uuid({ message: t("invalidFieldError") })
      .or(z.literal(""))
      .optional(),
    customerSegment: z.enum(CUSTOMER_SEGMENTS, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
    feePurpose: z.enum(FEE_PURPOSES, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
    calculationType: z.enum(FEE_CALCULATION_TYPES, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
    tierBasis: z.enum(TIER_BASIS, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
    /** % appliqué si calculationType = PERCENTAGE (0-100). */
    percentageValue: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") })
      .max(100, { message: t("invalidFieldError") })
      .optional(),
    /** Frais fixe (unité MAJEURE — converti en `fixedValueMinor` au submit). */
    fixedValue: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") })
      .optional(),
    /** Plancher (unité MAJEURE). */
    minFee: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") })
      .optional(),
    /** Plafond (unité MAJEURE). */
    maxFee: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") })
      .optional(),
    currency: z
      .string()
      .trim()
      .regex(/^[A-Z]{3}$/, { message: t("invalidFieldError") }),
    payer: z.enum(FEE_PAYERS_FEE_ENGINE, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
    status: z.enum(FEE_RULE_STATUSES, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
    effectiveFrom: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    effectiveTo: z.string().trim().or(z.literal("")).optional(),
  });

export type FeeRuleFormValues = z.infer<ReturnType<typeof createFeeRuleSchema>>;
